// Parse a Salesforce Flow `.flow-meta.xml` into React Flow nodes + edges for the
// Flow diagram preview. Browser DOMParser only — NO regex (project rule): we walk
// the parsed DOM. Layout is a simple top-down layering from the Start element so it
// reads like the Flow Builder auto-layout canvas.
import { MarkerType, type Edge, type Node } from '@xyflow/react';

export interface FlowGraph {
  nodes: Node[];
  edges: Edge[];
  label: string;
}

interface RawEdge {
  source: string;
  target: string;
  label?: string;
}

// Flow element collection tag → the node "kind" we render it as.
const COLLECTIONS: Record<string, string> = {
  screens: 'screen',
  decisions: 'decision',
  assignments: 'assignment',
  loops: 'loop',
  recordCreates: 'recordCreate',
  recordUpdates: 'recordUpdate',
  recordDeletes: 'recordDelete',
  recordLookups: 'recordLookup',
  actionCalls: 'action',
  apexPluginCalls: 'action',
  subflows: 'subflow',
  waits: 'wait',
  collectionProcessors: 'transform',
  customErrors: 'error',
  steps: 'step',
  orchestratedStages: 'stage',
};

function childText(el: Element, tag: string): string {
  for (let i = 0; i < el.children.length; i++) {
    const c = el.children[i];
    if (c.tagName === tag) return (c.textContent || '').trim();
  }
  return '';
}

function childEls(el: Element, tag: string): Element[] {
  const out: Element[] = [];
  for (let i = 0; i < el.children.length; i++) {
    if (el.children[i].tagName === tag) out.push(el.children[i] as Element);
  }
  return out;
}

function connectorTarget(el: Element, tag: string): string {
  const conns = childEls(el, tag);
  return conns.length ? childText(conns[0], 'targetReference') : '';
}

// Every outgoing connector from one element → raw edges.
function edgesFrom(el: Element, sourceId: string): RawEdge[] {
  const out: RawEdge[] = [];
  const add = (target: string, label?: string) => {
    if (target) out.push({ source: sourceId, target, label });
  };
  add(connectorTarget(el, 'connector'));
  add(connectorTarget(el, 'nextValueConnector'), 'For Each');
  add(connectorTarget(el, 'noMoreValuesConnector'), 'After Last');
  add(connectorTarget(el, 'faultConnector'), 'Fault');
  for (const rule of childEls(el, 'rules')) {
    add(connectorTarget(rule, 'connector'), childText(rule, 'label') || 'Rule');
  }
  add(connectorTarget(el, 'defaultConnector'), childText(el, 'defaultConnectorLabel') || 'Default');
  for (const ev of childEls(el, 'waitEvents')) {
    add(connectorTarget(ev, 'connector'), childText(ev, 'label') || 'Event');
  }
  for (const sp of childEls(el, 'scheduledPaths')) {
    add(connectorTarget(sp, 'connector'), childText(sp, 'label') || 'Scheduled');
  }
  return out;
}

// Top-down layered layout: depth = BFS distance from start; x = slot within depth.
function layout(ids: string[], edges: RawEdge[], startId: string): Map<string, { x: number; y: number }> {
  const adj = new Map<string, string[]>();
  ids.forEach((id) => adj.set(id, []));
  edges.forEach((e) => { if (adj.has(e.source)) adj.get(e.source)!.push(e.target); });
  const depth = new Map<string, number>();
  const queue: string[] = [];
  const root = startId && ids.includes(startId) ? startId : ids[0];
  if (root) { depth.set(root, 0); queue.push(root); }
  while (queue.length) {
    const cur = queue.shift() as string;
    const d = depth.get(cur) ?? 0;
    for (const nxt of adj.get(cur) || []) {
      if (!depth.has(nxt)) { depth.set(nxt, d + 1); queue.push(nxt); }
    }
  }
  let maxD = 0;
  depth.forEach((d) => { if (d > maxD) maxD = d; });
  ids.forEach((id) => { if (!depth.has(id)) depth.set(id, maxD + 1); });
  const perDepth = new Map<number, number>();
  const NODE_W = 240;
  const NODE_H = 130;
  const pos = new Map<string, { x: number; y: number }>();
  ids.forEach((id) => {
    const d = depth.get(id) ?? 0;
    const slot = perDepth.get(d) ?? 0;
    perDepth.set(d, slot + 1);
    pos.set(id, { x: slot * NODE_W, y: d * NODE_H });
  });
  return pos;
}

export function flowXmlToGraph(xml: string): FlowGraph | null {
  if (!xml || xml.indexOf('<Flow') === -1) return null;
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(xml, 'application/xml');
  } catch {
    return null;
  }
  if (doc.getElementsByTagName('parsererror').length) return null;
  const flow = doc.documentElement;
  if (!flow || flow.tagName !== 'Flow') return null;

  const label = childText(flow, 'label') || 'Flow';
  const rawNodes: { id: string; kind: string; label: string }[] = [];
  const rawEdges: RawEdge[] = [];

  // Entry point. Modern flows have a <start> element (its connector / scheduledPaths
  // point at the first element). Legacy Process-Builder-migrated flows have NO <start>
  // and instead use a top-level <startElementReference> naming the first element.
  const startEls = childEls(flow, 'start');
  const startRef = childText(flow, 'startElementReference');
  let startId = '';
  if (startEls.length) {
    startId = childText(startEls[0], 'name') || '__start__';
    rawNodes.push({ id: startId, kind: 'start', label: 'Start' });
    const before = rawEdges.length;
    rawEdges.push(...edgesFrom(startEls[0], startId));
    // <start> with only trigger metadata (no connector) → fall back to the legacy pointer.
    if (rawEdges.length === before && startRef) {
      rawEdges.push({ source: startId, target: startRef });
    }
  } else if (startRef) {
    startId = '__start__';
    rawNodes.push({ id: startId, kind: 'start', label: 'Start' });
    rawEdges.push({ source: startId, target: startRef });
  }
  for (const [tag, kind] of Object.entries(COLLECTIONS)) {
    for (const el of childEls(flow, tag)) {
      const id = childText(el, 'name');
      if (!id) continue;
      rawNodes.push({ id, kind, label: childText(el, 'label') || id });
      rawEdges.push(...edgesFrom(el, id));
    }
  }
  if (!rawNodes.length) return null;

  const ids = new Set(rawNodes.map((n) => n.id));
  const kept = rawEdges.filter((e) => ids.has(e.source) && ids.has(e.target));
  const pos = layout([...ids], kept, startId);

  const nodes: Node[] = rawNodes.map((n) => ({
    id: n.id,
    type: 'flowElement',
    position: pos.get(n.id) || { x: 0, y: 0 },
    data: { kind: n.kind, label: n.label },
  }));
  const edges: Edge[] = kept.map((e, i) => ({
    id: `e${i}-${e.source}-${e.target}`,
    source: e.source,
    target: e.target,
    label: e.label,
    type: 'smoothstep',
    markerEnd: { type: MarkerType.ArrowClosed },
  }));

  return { nodes, edges, label };
}
